import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Paper } from "@material-ui/core";
import { chartBars } from "./statBarsConfig";
import { FilesSumData, parseData } from "./parseData";
import ChartHeader from "../ChartHeader";
import takeRange from "../takeRange";
import { Margin } from "recharts/types/util/types";
import { StatRecord } from "@dashboard-v3/api";
import { LegendData, StatConfig } from "../types";
import { DaysRange } from "../../dayRange";
import {
  formatBigNumber,
  tickFormatter,
  tooltipFormatter,
  tooltipLabelFormatter,
} from "../labels";
import ChartTitle from "../ChartTitle";

dayjs.extend(utc);

type Params = {
  stats: Array<StatRecord>;
  range: DaysRange;
};

export default function UploadedFilesChart({ stats, range = 90 }: Params) {
  const { t } = useTranslation("usageReports");
  const [data, setData] = useState<FilesSumData>([]);
  const [statBars, setStatBars] = useState<StatConfig[]>(chartBars);
  const chartMargins: Margin = { top: 5, right: 30, bottom: 5, left: 5 };

  useEffect(() => {
    setData(parseData(stats));
  }, [stats]);

  const handleLegendClick = (data: LegendData) => {
    setStatBars(prev => {
      return prev.map(statBar => {
        const { dataKey } = statBar;
        if (dataKey === data.dataKey) {
          statBar.enabled = !statBar.enabled;
        }
        return statBar;
      });
    });
  };

  return (
    <Paper style={{ padding: "2rem 1rem", width: "100%" }}>
      <ChartHeader>
        <ChartTitle>{t("uploadedFiles.title")}</ChartTitle>
      </ChartHeader>
      <ResponsiveContainer height={250}>
        <BarChart data={takeRange(data, range)} margin={chartMargins}>
          <CartesianGrid strokeDasharray="4" vertical={false} />
          <XAxis dataKey="date" tickFormatter={tickFormatter} />
          <YAxis tickLine={false} tickFormatter={formatBigNumber} />
          <Tooltip
            labelFormatter={tooltipLabelFormatter}
            formatter={tooltipFormatter}
          />
          <Legend onClick={handleLegendClick} />
          {statBars.map(stat => (
            <Bar
              key={stat.dataKey}
              dataKey={stat.dataKey}
              name={t(stat.name)}
              stackId="a"
              fill={stat.fill}
              fillOpacity={0.8}
              hide={!stat.enabled}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
}
